import { Component, computed, inject, Input } from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { forkJoin } from 'rxjs';
import { publicMethods } from '../../../globals';
import { concatMap, tap } from 'rxjs/operators';
import { I18nPluralPipe } from '@angular/common';
import { pluralMappings } from '../../../classes/i18n';

@Component({
    selector: 'wizard-crm-status',
    templateUrl: './wizard-crm-status.component.html',
    styleUrl: './wizard-crm-status.component.css',
    standalone: false
})
export class WizardCrmStatusComponent {
  @Input() stepData: Record<string, any>[] = [];

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  pluralPipe = inject(I18nPluralPipe);
  pluralMappings = pluralMappings;

  CRM_OBJECT_TYPE = 'crm_object_type';
  CRM_OBJECT_FIELDS = 'crm_object_fields';
  EXPORT_MAPPINGS = 'export_mappings';
  IMPORT_MAPPINGS = 'import_mappings';
  EXPORT_PERMISSIONS = 'export_permissions';
  LIST_SYNC_LOGS = 'list_sync_logs';
  ENRICHMENT_SUMMARY = 'enrichment_summary';
  COMPANY_MATCHER_STATUS = 'company_matcher_status';

  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });
  exportPermissionId: number = null;

  dataToExcludeInOverview = ['auth', 'export_mapping', 'export_permissions'];
  mapsAndPermissionsTypes = [
    'export_mapping',
    'export_permissions',
    'import_config',
  ];

  statusSummaryData = {
    overviewData: [],
    mappingsData: [],
    logHistory: [],
    enrichmentSummaryData: undefined,
    companyMatcherStatus: undefined,
  };

  importConfigEnabled = false;
  enrichmentEnabled = false;

  ngOnInit() {
    this.handleStepsData();
    this.getConfigSummary();
  }

  handleStepsData(): void {
    this.statusSummaryData.overviewData = this.stepData.filter(
      step => !this.dataToExcludeInOverview.includes(step.stepType)
    );
    this.statusSummaryData.mappingsData = this.stepData?.filter(step =>
      this.mapsAndPermissionsTypes.includes(step.stepType)
    );
  }

  getConfigSummary(): void {
    this.crmService
      .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
      .pipe(
        tap(data => {
          if (this.pubMethods.hasData(data) && data.length) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0].id);
            this.exportPermissionId = data[0].exporterPermissionsId;
            this.handleCRMObjectOverview(data);
          }
        }),
        concatMap(() => {
          return forkJoin({
            fieldData: this.crmService.getCRMExistingState({
              actionType: this.CRM_OBJECT_FIELDS,
              objectId: this.crmObjectID(),
              fieldId: null,
            }),
            exportMappingsData: this.crmService.getCRMExistingState({
              actionType: this.EXPORT_MAPPINGS,
              objectId: this.crmObjectID(),
            }),
            importMappingsData: this.crmService.getCRMExistingState({
              actionType: this.IMPORT_MAPPINGS,
              objectId: this.crmObjectID(),
            }),
            permissionsData: this.crmService.getCRMExistingState({
              actionType: this.EXPORT_PERMISSIONS,
              objectId: this.exportPermissionId,
            }),
            logData: this.crmService.getCRMExistingState({
              actionType: this.LIST_SYNC_LOGS,
              objectId: this.crmObjectID(),
            }),
            enrichmentSummaryData: this.crmService.getCRMExistingState({
              actionType: this.ENRICHMENT_SUMMARY,
              objectId: this.crmObjectID(),
            }),
            companyMatcherStatus: this.crmService.getCompanyMatcherStatus(this.crmObjectID()),
          });
        }),
        tap(results => {
          this.handleLookupFieldData(results.fieldData);
          this.handleMappingPermissionsData(results);
          this.statusSummaryData.logHistory = results.logData?.slice(0, 5);
          this.statusSummaryData.enrichmentSummaryData = results.enrichmentSummaryData;
          this.statusSummaryData.companyMatcherStatus = results.companyMatcherStatus;

          this.statusSummaryData.overviewData.forEach(stepObj => {
            if (stepObj.stepType === 'company_matcher') {
              stepObj.overviewState = results.companyMatcherStatus;
            }
          });
        })
      )
      .subscribe();
  }

  handleCRMObjectOverview(objectData: Record<string, any>[]): void {
    if (!this.pubMethods.hasData(objectData)) return;

    this.statusSummaryData.overviewData.forEach(stepObj => {
      switch (stepObj.stepType) {
        case 'object_type':
          stepObj.overviewState = objectData[0].label;
          break;
        case 'superuser_token':
          stepObj.overviewState = objectData[0].useAdminCredentialsFlag;
          break;
        case 'enrichment':
          stepObj.overviewState = objectData[0].enrichEnabled;
          this.enrichmentEnabled = objectData[0].enrichEnabled;
          break;
        case 'import_config':
          stepObj.overviewState = objectData[0].importEnabled;
          this.importConfigEnabled = objectData[0].importEnabled;
          break;

        default:
          break;
      }
    });
  }

  handleLookupFieldData(lookupFieldData: Record<string, any>[]) {
    if (lookupFieldData?.length) {
      const filtered = lookupFieldData.filter(field => field.lookupField === 1);
      this.statusSummaryData.overviewData.forEach(obj => {
        if (obj.stepType === 'lookup_field') {
          obj.overviewState = filtered[0]?.label;
        }
      });
    }
  }

  handleMappingPermissionsData(apiData: Record<string, any>) {
    const { exportMappingsData, importMappingsData, permissionsData } = apiData;

    this.statusSummaryData.mappingsData.forEach(mappingData => {
      switch (mappingData.stepType) {
        case 'export_mapping':
          mappingData.overviewDetails = exportMappingsData;
          break;
        case 'export_permissions':
          mappingData.overviewDetails = permissionsData;
          break;
        case 'import_config':
          mappingData.overviewDetails = importMappingsData;
          mappingData.showNoConfigWarning =
            importMappingsData.length && !this.importConfigEnabled;
          break;

        default:
          break;
      }
    });
  }

  editStep(stepType: string) {
    const stepIndex = this.stepData.findIndex(
      step => step.stepType === stepType
    );

    this.crmService.specificConfigWizardStep.set(stepIndex);
  }

  renderMappingDetails(
    stepType: string,
    overviewDetails: Record<string, any>
  ): string {
    if (!this.pubMethods.hasData(overviewDetails)) return 'Not Configured';

    switch (stepType) {
      case 'export_mapping':
        const exportFieldsFormatted = this.pluralPipe.transform(
          overviewDetails.length,
          pluralMappings.field
        );
        return `${overviewDetails.length} ${exportFieldsFormatted} mapped`;

      case 'import_config':
        const importFieldsFormatted = this.pluralPipe.transform(
          overviewDetails.length,
          pluralMappings.field
        );
        return `${overviewDetails.length} ${importFieldsFormatted} mapped`;

      case 'export_permissions':
        const { viewableByOrganisations, viewableByUsers } = overviewDetails;
        const teamsFormatted = this.pluralPipe.transform(
          viewableByOrganisations.length,
          pluralMappings.team
        );
        const usersFormatted = this.pluralPipe.transform(
          viewableByUsers.length,
          pluralMappings.individual
        );
        return `${viewableByOrganisations.length} ${teamsFormatted} & ${viewableByUsers.length} ${usersFormatted}`;

      default:
        break;
    }
  }

  renderCRMObjectIcons(statusType: string, size = 'md', weight = 'l'): string {
    if (!statusType) return;

    switch (statusType) {
      case 'object_type':
        return `fa${weight} fa${size} fa-objects-column`;

      case 'superuser_token':
        return `fa${weight} fa${size} fa-user-key`;

      case 'lookup_field':
        return `fa${weight} fa${size} fa-magnifying-glass-arrows-rotate`;

      case 'enrichment':
        return `fa${weight} fa${size} fa-hexagon-nodes`;

      case 'import_config':
        return `fa-kit fa-light-database-circle-arrow-left`;

      case 'export_mapping':
        return `fa${weight} fa${size} fa-box-arrow-down-arrow-up`;

      case 'export_permissions':
        return `fa${weight} fa${size} fa-users-gear`;

      case 'company_matcher':
        return `fa${weight} fa${size} fa-users-between-lines`;

      default:
        return `fa${weight} fa${size} fa-objects-column`;
    }
  }
}
